import type { InputHTMLAttributes } from 'react'
import { utcIsoToZonedValue, zonedValueToUtcIso } from './timezone'

/** Default styling — replaced entirely (not merged) when `className` is provided. */
const DEFAULT_INPUT_CLASSES =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50'

/**
 * Browsers don't give the native `datetime-local` calendar-picker icon a hand cursor by
 * default. Always merged in (never replaced by `className`) so every date/time field gets it,
 * regardless of how it's styled.
 */
const CALENDAR_PICKER_CURSOR_CLASSES = '[&::-webkit-calendar-picker-indicator]:cursor-pointer'

/** Props for {@link DateTimeInput}. */
export interface DateTimeInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  /** Absolute instant as a UTC ISO string (e.g. `"2026-09-01T10:00:00.000Z"`), or `''`/`undefined`. */
  value?: string
  /** Called with the new absolute instant as a UTC ISO string, or `''` when the field is cleared. */
  onChange: (isoUtc: string) => void
  /** IANA timezone (e.g. `"Asia/Tokyo"`) used to interpret and display the wall-clock value. */
  timezone: string
}

/**
 * Shared date & time input, reused everywhere the app lets users pick a moment in time.
 *
 * The input itself only understands local wall-clock time, so this component converts
 * transparently between that and an absolute UTC instant using `timezone` — callers always
 * read/write plain UTC ISO strings and never need to think about offsets or DST themselves.
 * Pair it with `TimezoneSelect` to let the user choose which timezone applies.
 */
export function DateTimeInput({
  value,
  onChange,
  timezone,
  className = DEFAULT_INPUT_CLASSES,
  ...rest
}: DateTimeInputProps) {
  return (
    <input
      {...rest}
      type="datetime-local"
      value={utcIsoToZonedValue(value ?? '', timezone)}
      onChange={(e) => {
        const next = e.target.value
        onChange(next ? zonedValueToUtcIso(next, timezone) : '')
      }}
      className={`${className} ${CALENDAR_PICKER_CURSOR_CLASSES}`}
    />
  )
}
